import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchRoomMappings, 
  saveRoomMappings, 
  deleteRoomMappings,
  RoomMapping,
  SaveRoomMappingsParams 
} from '@/api/room-mappings';
import { toast } from '@/hooks/use-toast';

/**
 * Hook to fetch room mappings for a branch
 */
export function useRoomMappings(branchId: number) {
  return useQuery({
    queryKey: ['room-mappings', branchId],
    queryFn: () => fetchRoomMappings(branchId),
    enabled: !!branchId,
    select: (response) => response.data,
  });
}

/**
 * Hook to save room mappings
 */
export function useSaveRoomMappings(branchId: number, branchName: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { competitorHotelName: string; mappings: RoomMapping[] }) => 
      saveRoomMappings({
        branchId,
        branchName,
        competitorHotelName: params.competitorHotelName,
        mappings: params.mappings,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['room-mappings'] });
      toast({
        title: 'Mappings Saved',
        description: 'Room mappings have been saved successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save room mappings.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to delete all room mappings
 */
export function useDeleteRoomMappings(propertyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteRoomMappings(propertyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['room-mappings', propertyId] });
      toast({
        title: 'Mappings Deleted',
        description: 'All room mappings have been deleted.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete room mappings.',
        variant: 'destructive',
      });
    },
  });
}
